import { Injectable } from '@angular/core';
import { HttpEvent, HttpInterceptor, HttpHandler, HttpRequest, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { Router } from '@angular/router';
import { MessageService } from 'primeng/api';
import { AuthService } from '../services/AuthService';
import { environment } from '../../environments/environment';
import { isAccountUrl, passwordChangeUrl } from '../auth/password-change.guard';

/** Код 403 от бэка, пока сотрудник не сменил выданный администратором пароль. */
export const PASSWORD_CHANGE_REQUIRED_CODE = 'password_change_required';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {

  // Дедуп тостов «Недостаточно прав»: на странице может прилететь
  // несколько параллельных 403 — пользователю достаточно одного сообщения.
  private static readonly TOAST_403_COOLDOWN_MS = 2000;
  private last403At = 0;

  constructor(
    private authService: AuthService,
    private router: Router,
    private messageService: MessageService
  ) {}

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const token = this.authService.getToken();

    const clonedRequest = (token && this.isApiRequest(req.url))
      ? req.clone({ setHeaders: { Authorization: `Bearer ${token}` } })
      : req;

    return next.handle(clonedRequest).pipe(
      catchError((error: HttpErrorResponse) => {
        if (this.shouldSkip(req.url)) {
          return throwError(() => error);
        }

        if (error.status === 401) {
          this.authService.clearSession();
          this.router.navigate(['/login']);
        } else if (error.status === 403) {
          if (this.isPasswordChangeRequired(error)) {
            this.redirectToPasswordChange();
          } else if (!this.isDomainForbidden(error)) {
            this.maybeShowForbiddenToast(error);
            this.authService.refreshIfStale();
          }
        }

        return throwError(() => error);
      })
    );
  }

  private maybeShowForbiddenToast(error: HttpErrorResponse): void {
    const now = Date.now();
    if (now - this.last403At < AuthInterceptor.TOAST_403_COOLDOWN_MS) return;
    this.last403At = now;
    this.messageService.add({
      key: 'global',
      severity: 'error',
      summary: 'Недостаточно прав',
      detail: error.error?.message || 'У вас нет доступа к этому действию'
    });
  }

  private isPasswordChangeRequired(error: HttpErrorResponse): boolean {
    return error.error?.code === PASSWORD_CHANGE_REQUIRED_CODE;
  }

  // Только сотрудники: клиентский кабинет этот сценарий не затрагивает.
  private redirectToPasswordChange(): void {
    if (!this.authService.isStaff() || this.authService.hasRole('client')) return;
    this.authService.setMustChangePassword(true);
    if (isAccountUrl(this.router.url)) return;
    this.router.navigateByUrl(passwordChangeUrl(this.router));
  }

  private isDomainForbidden(error: HttpErrorResponse): boolean {
    return error.error?.code === 'ORDERING_CLOSED';
  }

  // Не обрабатываем 401/403 от /login (login.component сам показывает ошибку)
  // и от /me (auth-init.factory сам чистит сессию). Иначе цикл /me → 403 → /me.
  private shouldSkip(url: string): boolean {
    return url.endsWith('/login') || url.endsWith('/me');
  }

  // Цепляем Bearer-токен только к запросам на наш API (origin + префикс пути из environment.apiUrl).
  // Защищает от утечки токена в статику (/index.html, /assets/*) и на сторонние домены.
  private isApiRequest(url: string): boolean {
    try {
      const target = new URL(url, window.location.origin);
      const api = new URL(environment.apiUrl, window.location.origin);
      return target.origin === api.origin && target.pathname.startsWith(api.pathname);
    } catch {
      return false;
    }
  }
}
